import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, TruckIcon } from '@heroicons/react/20/solid';
import { Fragment } from 'react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const VehicleTable = ({
  vehicles,
  selectedCustomer,
  navigate,
  handleScheduleAppointment,
  viewInspectionReport,
  deleteVehicle,
  openEditModal,
  openPhotosModal,
}) => {
  const filteredVehicles = vehicles.filter(
    (vehicle) => vehicle.customerId === (selectedCustomer?.value || '')
  );

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl">
            <TruckIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white">
              Customer Vehicles
            </h2>
            <p className="text-blue-100 text-sm">
              {selectedCustomer
                ? `Vehicles owned by ${selectedCustomer.label}`
                : 'Select a customer to view their vehicles'}
            </p>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="overflow-x-auto">
        {!selectedCustomer ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <TruckIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              No Customer Selected
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              Please select a customer from the form on the left to view and manage their vehicles
            </p>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="p-4 bg-blue-50 rounded-full mb-4">
              <TruckIcon className="h-12 w-12 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              No Vehicles Found
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              This customer doesn't have any vehicles registered yet. Add their first vehicle using the form.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-700 uppercase tracking-wider">
                  Make & Model
                </th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-700 uppercase tracking-wider">
                  Year
                </th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-700 uppercase tracking-wider">
                  Registration
                </th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-700 uppercase tracking-wider">
                  GIN
                </th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVehicles.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  className="hover:bg-blue-50 transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                        <TruckIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-gray-900">
                          {vehicle.vehicleMake} {vehicle.vehicleModel}
                        </div>
                        <div className="text-xs text-gray-500">
                          {vehicle.engineType || 'Engine type not specified'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                      {vehicle.year}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {vehicle?.registrationNumber || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded inline-block">
                      {vehicle?.GIN}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Menu as="div" className="relative inline-block text-left">
                      <div>
                        <Menu.Button className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 px-4 py-2 text-sm font-bold text-white shadow-md hover:from-blue-600 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                          Actions
                          <ChevronDownIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </Menu.Button>
                      </div>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden">
                          <div className="py-1">
                            {[
                              {
                                name: 'Diagnostic Questionnaire',
                                action: () =>
                                  navigate('/vehicles/diagnosisHistory', {
                                    state: { vehicleId: vehicle.id },
                                  }),
                                icon: '🔍',
                                color: 'hover:bg-blue-50 hover:text-blue-700',
                              },
                              {
                                name: 'Edit Vehicle Details',
                                action: () => openEditModal(vehicle),
                                icon: '✏️',
                                color: 'hover:bg-purple-50 hover:text-purple-700',
                              },
                              {
                                name: 'Manage Photos',
                                action: () => openPhotosModal(vehicle),
                                icon: '📷',
                                color: 'hover:bg-emerald-50 hover:text-emerald-700',
                              },
                              {
                                name: 'Service History',
                                action: () => navigate(`/vehicleHistory/${vehicle.id}`),
                                icon: '📋',
                                color: 'hover:bg-indigo-50 hover:text-indigo-700',
                              },
                              {
                                name: 'Schedule Appointment',
                                action: () => handleScheduleAppointment(vehicle),
                                icon: '📅',
                                color: 'hover:bg-cyan-50 hover:text-cyan-700',
                              },
                              {
                                name: 'View Inspection Report',
                                action: () => viewInspectionReport(vehicle),
                                icon: '📄',
                                color: 'hover:bg-teal-50 hover:text-teal-700',
                              },
                              {
                                name: 'Inspect Vehicle',
                                action: () => navigate('/inspection', { state: { vehicle } }),
                                icon: '🔧',
                                color: 'hover:bg-orange-50 hover:text-orange-700',
                              },
                              {
                                name: 'Delete Vehicle',
                                action: () => {
                                  if (window.confirm(`Are you sure you want to delete ${vehicle.vehicleMake} ${vehicle.vehicleModel}?`)) {
                                    deleteVehicle(vehicle.id, vehicle.customerId);
                                  }
                                },
                                icon: '🗑️',
                                color: 'hover:bg-red-50 hover:text-red-700',
                              },
                            ].map((item) => (
                              <Menu.Item key={item.name}>
                                {({ active }) => (
                                  <button
                                    onClick={item.action}
                                    className={classNames(
                                      'flex items-center w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150',
                                      item.color,
                                      active ? item.color : 'text-gray-700'
                                    )}
                                  >
                                    <span className="mr-3 text-lg">{item.icon}</span>
                                    {item.name}
                                  </button>
                                )}
                              </Menu.Item>
                            ))}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      {selectedCustomer && filteredVehicles.length > 0 && (
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <p className="text-sm text-gray-700">
            Showing <span className="font-bold">{filteredVehicles.length}</span> vehicle{filteredVehicles.length !== 1 ? 's' : ''} for <span className="font-bold">{selectedCustomer.label}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default VehicleTable;
